// src/utils/ws_peticiones_evento.js
//
// Maneja tres tipos de conexión WebSocket:
//   - tatami:    juez activo/standby, bidireccional
//   - publico:   pantalla TV, solo lectura
//   - dashboard: admin dashboard, escucha eventos de rebalanceo

const MAX_RETRIES = 5
const BASE_DELAY  = 1000  // ms

// ── Estado interno por canal ───────────────────────────────────────────────
function crearCanal() {
  return {
    socket:         null,
    config:         null,
    reconnectTimer: null,
    retries:        0,
    cerradoManual:  false,
  }
}

const canal = {
  tatami:    crearCanal(),
  publico:   crearCanal(),
  dashboard: crearCanal(),
}

// ── Helpers internos ───────────────────────────────────────────────────────

function _connect(tipo) {
  const c = canal[tipo]
  if (!c.config) return

  const { tatamiId, torneoId, onMessage, onOpen, onClose, onError, onRolAsignado } = c.config

  let url
  const base = process.env.VUE_APP_WS ||
  `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}`

  if (tipo === 'tatami') {
    const token = localStorage.getItem('evento_token')
    if (!token) {
      console.warn('[WS:tatami] No hay token — abortando conexión')
      return
    }
    url = `${base}/ws/tatami/${tatamiId}/?token=${token}`
  } else if (tipo === 'dashboard') {
    // Dashboard se conecta al mismo endpoint que público pero identificado
    // Se suscribe al grupo proximas_peleas_{torneoId} para recibir eventos de rebalanceo
    url = `${base}/ws/publico/`
  } else {
    url = `${base}/ws/publico/`
  }

  c.socket = new WebSocket(url)

  c.socket.onopen = () => {
    c.retries       = 0
    c.cerradoManual = false
    _clearTimer(tipo)
    console.info(`[WS:${tipo}] Conectado`)
    if (onOpen) onOpen()
  }

  c.socket.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data)

      // Manejar rol asignado al conectar (solo tatami)
      if (data.type === 'rol_asignado' && onRolAsignado) {
        onRolAsignado(data.rol)
        return
      }

      if (onMessage) onMessage(data)
    } catch (e) {
      console.error(`[WS:${tipo}] Error parseando mensaje:`, e)
    }
  }

  c.socket.onclose = (event) => {
    console.warn(`[WS:${tipo}] Cerrado — código ${event.code}`)
    if (onClose) onClose(event)

    // No reconectar si fue cierre manual o se agotaron reintentos
    if (c.cerradoManual || event.code === 1000) return

    if (c.retries < MAX_RETRIES) {
      c.retries++
      const delay = Math.min(BASE_DELAY * c.retries, 8000)
      console.warn(`[WS:${tipo}] Reconectando en ${delay}ms (intento ${c.retries}/${MAX_RETRIES})`)
      c.reconnectTimer = setTimeout(() => _connect(tipo), delay)
    } else {
      console.error(`[WS:${tipo}] Máximo de reintentos alcanzado`)
      if (onError) onError(new Error('Máximo de reintentos alcanzado'), c.retries)
    }
  }

  c.socket.onerror = (error) => {
    console.error(`[WS:${tipo}] Error:`, error)
    if (onError) onError(error, c.retries)
  }
}

function _clearTimer(tipo) {
  const c = canal[tipo]
  if (c.reconnectTimer) {
    clearTimeout(c.reconnectTimer)
    c.reconnectTimer = null
  }
}

function _close(tipo) {
  const c = canal[tipo]
  _clearTimer(tipo)
  c.retries       = MAX_RETRIES
  c.cerradoManual = true
  if (c.socket) {
    c.socket.close(1000, 'Cierre manual')
    c.socket = null
  }
  c.config = null
}

function _send(tipo, payload) {
  const c = canal[tipo]
  if (c.socket && c.socket.readyState === WebSocket.OPEN) {
    c.socket.send(JSON.stringify(payload))
  } else {
    console.warn(`[WS:${tipo}] Socket no disponible — mensaje descartado:`, payload)
  }
}

function _getState(tipo) {
  const c = canal[tipo]
  if (!c.socket) return 'NO_SOCKET'
  return ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][c.socket.readyState] || 'UNKNOWN'
}


// ── API PÚBLICA — Tatami (juez) ────────────────────────────────────────────

/**
 * Conecta al WebSocket del tatami como juez.
 *
 * @param {Object}   config
 * @param {number}   config.tatamiId
 * @param {Function} config.onMessage      - recibe el objeto JSON parseado
 * @param {Function} [config.onRolAsignado] - recibe 'activo' | 'standby'
 * @param {Function} [config.onOpen]
 * @param {Function} [config.onClose]
 * @param {Function} [config.onError]
 */
export function connectTatami(config) {
  canal.tatami.config = config
  canal.tatami.retries = 0
  canal.tatami.cerradoManual = false
  _connect('tatami')
}

/**
 * Envía una acción al servidor (solo juez activo puede actuar).
 * Acciones disponibles:
 *   modificar_marcador → { accion, pelea_id, tipo, color, valor, operacion }
 *   control_tiempo     → { accion, pelea_id, operacion: 'iniciar'|'pausar' }
 *   finalizar_pelea    → { accion, pelea_id, ganador: 'rojo'|'azul', tipo }
 */
export function sendTatami(payload) {
  _send('tatami', payload)
}

export function closeTatami() {
  _close('tatami')
}

export function getTatamiState() {
  return _getState('tatami')
}


// ── API PÚBLICA — Público (pantalla TV / próximos) ─────────────────────────

/**
 * Conecta al WebSocket público (solo lectura).
 *
 * @param {Object}   config
 * @param {Function} config.onMessage  - recibe el objeto JSON parseado
 * @param {Function} [config.onOpen]
 * @param {Function} [config.onClose]
 * @param {Function} [config.onError]
 */
export function connectPublico(config) {
  canal.publico.config = config
  canal.publico.retries = 0
  canal.publico.cerradoManual = false
  _connect('publico')
}

export function closePublico() {
  _close('publico')
}

export function getPublicoState() {
  return _getState('publico')
}


// ── API PÚBLICA — Dashboard (admin) ────────────────────────────────────────

/**
 * Conecta al WebSocket para el dashboard admin.
 * Escucha eventos de rebalanceo de tatamis (cola_tatami_update).
 *
 * @param {Object}   config
 * @param {number}   config.torneoId   - ID del torneo activo
 * @param {Function} config.onMessage  - recibe el objeto JSON parseado
 * @param {Function} [config.onOpen]
 * @param {Function} [config.onClose]
 * @param {Function} [config.onError]
 */
export function connectDashboard(config) {
  canal.dashboard.config = config
  canal.dashboard.retries = 0
  canal.dashboard.cerradoManual = false
  _connect('dashboard')
}

export function closeDashboard() {
  _close('dashboard')
}

export function getDashboardState() {
  return _getState('dashboard')
}


// ── Helpers de acciones (conveniencia para el juez) ────────────────────────

export const accionJuez = {
  /**
   * @param {number} peleaId
   * @param {'puntos'|'ventaja'|'amonestacion'} tipo
   * @param {'rojo'|'azul'} color
   * @param {number} valor  - 2 o 3 para puntos, 1 para ventaja/amonestación
   * @param {'sumar'|'restar'} operacion
   */
  modificarMarcador(peleaId, tipo, color, valor = 1, operacion = 'sumar') {
    sendTatami({ accion: 'modificar_marcador', pelea_id: peleaId, tipo, color, valor, operacion })
  },

  iniciarCronometro(peleaId) {
    sendTatami({ accion: 'control_tiempo', pelea_id: peleaId, operacion: 'iniciar' })
  },

  pausarCronometro(peleaId) {
    sendTatami({ accion: 'control_tiempo', pelea_id: peleaId, operacion: 'pausar' })
  },

  /**
   * @param {number} peleaId
   * @param {'rojo'|'azul'} ganador
   * @param {'puntos'|'sumision'|'descalificacion'|'walkover'} tipo
   */
  finalizarPelea(peleaId, ganador, tipo = 'puntos') {
    sendTatami({ accion: 'finalizar_pelea', pelea_id: peleaId, ganador, tipo })
  },
}